import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import HipPractitionerPicker from './HipPractitionerPicker';
import {
  getInvoice, getInvoicePrefill, createInvoice, updateInvoice, linkInvoice,
} from '../../../services/api/hipInvoicesApi';
import {
  INVOICE_TYPE, INVOICE_TYPES_BY_KIND, INVOICE_STATUS, PREFILL_KIND,
} from '../../../constants/hipInvoiceEnums';
import {
  ENCOUNTER_KIND, ABHA_STATUS, SLOT_STATE, PRACTITIONER_REQUIRED,
} from '../../../constants/hipRecordEnums';
import {
  INVOICE, INVOICE_TYPE_LABEL, CARD, LIST, PRACTITIONER, ABHA_VERIFIED, ABHA_NOT_VERIFIED, incompleteRecordText,
} from '../../../constants/hipRecordsCopy';
import { parseRupeesToPaise, paiseToRupeeInput, computeInvoiceTotals } from '../../../utils/invoiceTotals';
import { formatPaise } from '../../../utils/money';
import { formatDateIST, formatDateTimeISTWithYearNoSeconds } from '../../../utils/formatIST';
import { formatToken } from '../../../utils/formatToken';
import { pausedSentenceOf } from './slotTime';

/**
 * HipInvoiceDrawer - INV-F1. The structured Invoice form (OR-20..OR-24): side drawer from sm up,
 * bottom sheet on phones, portal on document.body.
 *
 * Props (frozen, build-plan section 5 INV): { open, record, invoice|null, suggestion|null, onClose, onDone(invoice, linkInfo?) }
 * plus the optional `slot` (OR-108).
 *   - invoice: an existing draft / linked invoice. A full Invoice (with `lines`) is shown as given; a
 *     slot summary (no `lines`, A1.2) is fetched ONCE on open via getInvoice.
 *   - slot: the card's checklist slot of that invoice (the very object its row reads; none for a suggestion or a blank form). While
 *     it is PAUSED (the patient is blocked by ABDM, or a rate-limit pause) "Link to ABHA" - and "Retry link" on a frozen invoice - is
 *     disabled and the reason line says what the row says (pausedSentenceOf: same words, same time). Editing and saving stay open,
 *     and no doctor is asked for (the sentence replaces that reason; the picker may stay on the Doctor row).
 *   - suggestion: { sourceKind, sourceRef, billNumber } - lines fetched ONCE on open via getInvoicePrefill.
 *   - neither: a blank form, NO request.
 *   - Save draft -> create / update, then onDone(invoice) + onClose(). Link to ABHA -> confirm modal ->
 *     save (if new or changed) -> linkInvoice once -> onDone + onClose. If the save worked but the
 *     link failed, the drawer stays open (retry = link only); dismissing then still calls onDone(saved).
 *     A link that went through (Link to ABHA, Retry link) also hands onDone `linkInfo` { patientName, records: [{ invoiceId,
 *     name: the invoice row's head, state: the invoice's state from the link answer }] } - the shape a card link gives its
 *     parent, an invoice found again by invoiceId - so the list shows its link notice (OR-77, OR-93, OR-114).
 *     Save draft and a dismissal pass none.
 *   - LINKED -> read-only view of the frozen snapshot; Close is the only action.
 *   - A visit with no doctor (`record.needsPractitioner`, OR-59, OR-106): the Doctor row of the facts box IS the required
 *     "Doctor for patient" picker (same component and hint as the card's Link popup). "Link to ABHA" stays disabled -
 *     with the reason beside it - until one is picked, the Link confirm has no picker, and linkInvoice sends the pick as
 *     `practitionerId`. With a doctor on the visit nothing changes. A 422 PRACTITIONER_REQUIRED highlights and focuses the
 *     picker (no banner, no popup).
 *   - A frozen invoice (status LINKED) whose link failed - or never started (state READY) - is read-only but offers
 *     ONE "Retry link": one linkInvoice with NO doctor (R3: the care context keeps the first pick). Only if the
 *     server answers PRACTITIONER_REQUIRED (nothing stored) on a no-doctor visit does the picker appear on the Doctor
 *     row, highlighted; Retry link then waits for a pick (redesign v2 R-F4: the card no longer retries invoices itself).
 *
 * Money: inputs are typed rupees, turned into INTEGER paise by parseRupeesToPaise (never float);
 * totals are computeInvoiceTotals - the same formula as the server, display only (OR-24).
 * Rules mirror the server (INV-B3 invoice_rules): bill number 1..30, description 1..200,
 * amounts >= 0, rate or discount > 0 per line, sum(discount) <= sum(rate) at invoice level (OR-37:
 * a rate-0 "Concession" line is valid), gross > 0, CGST/SGST on Pharmacy only (OR-22). No line cap
 * (OR-43): a too-big draft is refused by the server (INVOICE_TOO_LARGE) and the message shown as-is.
 * No MRP column: the contract line has no MRP (INV-B1 never sends one).
 *
 * Long bills: each line is a memoised row with stable callbacks, so typing re-renders only that row
 * (1,500-line test). The column headings stick to the top of the scrolling body; totals + buttons
 * sit in the footer, always in view.
 */

const DRAWER_Z = 1000; // below ConfirmationModal (1100)
const DESCRIPTION_MAX = 200;
const BILL_NUMBER_MAX = 30;
const SUMMARY_MAX = 5; // error summary lists this many, then "+n more"
const BUSY = { SAVE: 'SAVE', LINK: 'LINK' };
const CONFIRM_KIND = { LINK: 'LINK', GST: 'GST', DISCARD: 'DISCARD' };
// Line input keys -> the server's camelCase field keys (err.fieldKey of hipInvoicesApi).
const FIELD_KEY = { description: 'description', rate: 'ratePaise', discount: 'discountPaise', cgst: 'cgstPaise', sgst: 'sgstPaise' };
const AMOUNT_KEYS = ['rate', 'discount', 'cgst', 'sgst'];
// hipApi's fallback classifications: no server wording behind them, so show our own message.
const GENERIC_CODES = new Set(['UNKNOWN', 'NETWORK_ERROR', 'NOT_FOUND', 'VALIDATION_ERROR', 'FORBIDDEN', 'SERVICE_UNAVAILABLE']);

// ── Row model (typed strings) <-> contract lines (integer paise) ──────────────────────────
let rowSeq = 0;
const newRow = (o = {}) => {
  rowSeq += 1;
  return { id: rowSeq, description: '', rate: '', discount: '', cgst: '', sgst: '', ...o };
};
const optionalInput = (paise) => (paise ? paiseToRupeeInput(paise) : ''); // 0 = left blank (optional)
const rowFromLine = (l) => newRow({
  description: l.description,
  rate: paiseToRupeeInput(l.ratePaise),
  discount: optionalInput(l.discountPaise),
  cgst: optionalInput(l.cgstPaise),
  sgst: optionalInput(l.sgstPaise),
});
const formFrom = (inv) => ({
  billNumber: inv.billNumber || '',
  invoiceType: inv.invoiceType || null,
  rows: inv.lines && inv.lines.length ? inv.lines.map(rowFromLine) : [newRow()],
});
const typedGst = (v) => v.trim() !== '' && parseRupeesToPaise(v) !== 0;

/** One typed amount -> { paise } or { error }. Blank = 0 unless required. */
function parseAmount(text, required) {
  const v = text.trim();
  if (v === '') return required ? { error: INVOICE.err.rateRequired } : { paise: 0 };
  if (v.startsWith('-')) return { error: INVOICE.err.negative };
  const paise = parseRupeesToPaise(v);
  return paise === null ? { error: INVOICE.err.notAmount } : { paise };
}

/** A row -> the contract line (paise; null where not an amount) + its client errors (null if none). */
function parseRow(row, gst) {
  const out = { description: row.description.trim() };
  const errors = {};
  if (!out.description) errors.description = INVOICE.err.description;
  AMOUNT_KEYS.forEach((k) => {
    if (!gst && (k === 'cgst' || k === 'sgst')) { out[FIELD_KEY[k]] = 0; return; }
    const r = parseAmount(row[k], k === 'rate');
    out[FIELD_KEY[k]] = r.error ? null : r.paise;
    if (r.error) errors[k] = r.error;
  });
  if (!errors.rate && !errors.discount && out.ratePaise === 0 && out.discountPaise === 0) errors.rate = INVOICE.err.lineAmount;
  return { line: out, errors: Object.keys(errors).length ? errors : null };
}

// ── Styles (Tailwind, solid light + dark pairs) ─────────────
const LABEL = 'block text-xs font-bold mb-1.5 text-slate-800 dark:text-slate-100';
const MUTED = 'text-xs text-slate-600 dark:text-slate-400';
const ERR = 'text-xs font-semibold text-red-700 dark:text-red-300';
const BTN = 'rounded-lg border border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 px-3 py-2 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed';
const BTN_SM = BTN.replace('px-3 py-2 text-sm', 'px-2.5 py-1.5 text-xs');
const BTN_PRIMARY = 'rounded-lg border border-teal-700 bg-teal-700 hover:bg-teal-800 text-white px-3 py-2 text-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed';
const INPUT = 'w-full rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-2 py-1.5 text-sm';
const inputClass = (bad) => `${INPUT} ${bad ? 'border-red-600 dark:border-red-400' : 'border-slate-400 dark:border-slate-500'}`;
const CARD_BOX = 'rounded-[10px] border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 p-3';
const CHIP_OK = 'rounded-full border px-2 py-0.5 text-[11px] font-bold border-emerald-400 bg-emerald-100 text-emerald-800 dark:border-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-200';
const CHIP_WARN = 'rounded-full border px-2 py-0.5 text-[11px] font-bold border-blue-300 bg-blue-100 text-blue-800 dark:border-blue-600 dark:bg-blue-900/40 dark:text-blue-200';
const CHIP_MUTED = 'rounded-full border px-2 py-0.5 text-[11px] font-bold border-slate-400 bg-white text-slate-700 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-200';
const TH = 'sticky top-0 z-[1] border-b border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-1.5 py-1.5 text-left text-[11px] font-bold text-slate-700 dark:text-slate-200 whitespace-nowrap';
const TD = 'px-1.5 py-1 align-top';

function typeChipClass(selected, off) {
  const base = 'rounded-full border px-3 py-1.5 text-xs font-bold min-h-[34px]';
  if (selected) return `${base} border-teal-700 bg-teal-700 text-white ${off ? 'cursor-default' : ''}`;
  return `${base} border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 ${off ? 'opacity-60 cursor-not-allowed' : 'hover:bg-slate-100 dark:hover:bg-slate-700'}`;
}

// ── One editable line (memoised: typing elsewhere does not re-render it) ────────────────────
const LineRow = memo(function LineRow({
  row, index, gst, canRemove, autoFocus, disabled, onChange, onRemove,
  errDescription, errRate, errDiscount, errCgst, errSgst,
}) {
  const n = index + 1;
  const amount = (key, col, err) => (
    <td className={TD}>
      <input
        type="text"
        inputMode="decimal"
        className={`${inputClass(!!err)} min-w-[84px] text-right`}
        aria-label={INVOICE.lineFieldLabel(n, col)}
        aria-invalid={err ? 'true' : undefined}
        value={row[key]}
        disabled={disabled}
        onChange={(e) => onChange(index, key, e.target.value)}
      />
    </td>
  );
  const errs = [errDescription, errRate, errDiscount, errCgst, errSgst].filter(Boolean);
  const len = row.description.length;
  return (
    <>
      <tr data-testid="invoice-line" className="border-b border-slate-200 dark:border-slate-700">
        <td className={`${TD} pt-2.5 text-xs font-semibold text-slate-600 dark:text-slate-400`}>{n}</td>
        <td className={`${TD} min-w-[180px]`}>
          <textarea
            className={`${inputClass(!!errDescription)} resize-none break-words`}
            rows={Math.min(4, Math.max(1, Math.ceil(len / 40)))}
            maxLength={DESCRIPTION_MAX}
            aria-label={INVOICE.lineFieldLabel(n, INVOICE.col.item)}
            aria-invalid={errDescription ? 'true' : undefined}
            value={row.description}
            disabled={disabled}
            autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus -- only the line just added by "+ Add line"
            onChange={(e) => onChange(index, 'description', e.target.value)}
          />
          {len >= DESCRIPTION_MAX - 40 && <span className={MUTED}>{INVOICE.descriptionCounter(len, DESCRIPTION_MAX)}</span>}
        </td>
        {amount('rate', INVOICE.col.rate, errRate)}
        {amount('discount', INVOICE.col.discount, errDiscount)}
        {gst && amount('cgst', INVOICE.col.cgst, errCgst)}
        {gst && amount('sgst', INVOICE.col.sgst, errSgst)}
        <td className={TD}>
          <button
            type="button"
            className={`${BTN_SM} h-8 w-8 !p-0`}
            aria-label={INVOICE.removeLine(n)}
            title={canRemove ? INVOICE.removeLine(n) : INVOICE.removeOnlyLine}
            disabled={disabled || !canRemove}
            onClick={() => onRemove(index)}
          >
            ✕
          </button>
        </td>
      </tr>
      {errs.length > 0 && (
        <tr>
          <td />
          <td colSpan={gst ? 6 : 4} className={`${TD} ${ERR}`} role="alert">{INVOICE.lineError(n, errs.join(' '))}</td>
        </tr>
      )}
    </>
  );
});

// ── Component ─────────────────────────────────────────────────────────────────────────────
export default function HipInvoiceDrawer({
  open, record, invoice, suggestion, slot, onClose, onDone,
}) {
  if (!open || !record) return null;
  // Remount per encounter / invoice / suggestion, so every open starts from clean state.
  return createPortal(
    <DrawerBody
      key={`${record.encounterKind}:${record.encounterId}:${invoice?.invoiceId ?? ''}:${suggestion?.sourceRef ?? ''}`}
      record={record}
      invoice={invoice || null}
      suggestion={suggestion || null}
      slot={slot || null}
      onClose={onClose}
      onDone={onDone}
    />,
    document.body,
  );
}

function DrawerBody({
  record, invoice, suggestion, slot, onClose, onDone,
}) {
  const given = invoice && Array.isArray(invoice.lines) ? invoice : null; // a full Invoice: no fetch
  const needsLoad = !given && !!(invoice || suggestion);
  const start = given ? formFrom(given) : { billNumber: '', invoiceType: null, rows: [newRow()] };

  const [loading, setLoading] = useState(needsLoad);
  const [loadError, setLoadError] = useState('');
  const [saved, setSaved] = useState(given); // the server's copy (null until first save)
  const [billNumber, setBillNumber] = useState(start.billNumber);
  const [invoiceType, setInvoiceType] = useState(start.invoiceType);
  const [rows, setRows] = useState(start.rows);
  const [dirty, setDirty] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [serverErr, setServerErr] = useState(null); // { fieldKey, lineIndex, message } of a 422
  const [banner, setBanner] = useState('');
  const [confirm, setConfirm] = useState(null); // null | CONFIRM_KIND.*
  const [practitionerId, setPractitionerId] = useState(''); // "Doctor for patient" picked on the Doctor row (OR-59, OR-64, OR-106)
  const [doctorAsked, setDoctorAsked] = useState(false); // a frozen invoice's retry was answered PRACTITIONER_REQUIRED: the picker shows
  const [doctorErr, setDoctorErr] = useState(false); // 422 PRACTITIONER_REQUIRED: the picker is highlighted until a doctor is picked
  const [pendingType, setPendingType] = useState(null);
  const [busy, setBusy] = useState(null);
  const [addedId, setAddedId] = useState(null);
  const busyRef = useRef(false); // single-flight: a double click never fires two requests
  const loadStartedRef = useRef(false); // one load per open (StrictMode re-runs effects)
  const unreportedRef = useRef(null); // saved here but not yet passed to onDone
  const asideRef = useRef(null);
  const bodyRef = useRef(null);
  const doctorBoxRef = useRef(null);
  const keyRef = useRef(null);
  const uid = useId();
  const headingId = `${uid}-heading`;
  const billId = `${uid}-bill`;
  const reasonId = `${uid}-reason`;

  const prefillSource = saved?.prefillSource
    ?? (suggestion ? { kind: suggestion.sourceKind, ref: suggestion.sourceRef } : { kind: PREFILL_KIND.NONE, ref: null });
  const readOnly = saved?.status === INVOICE_STATUS.LINKED;
  const canRetry = readOnly && (saved.state === SLOT_STATE.LINK_FAILED || saved.state === SLOT_STATE.READY);
  const gst = invoiceType === INVOICE_TYPE.PHARMACY;
  const isOpd = record.encounterKind === ENCOUNTER_KIND.OPD;
  const verified = record.abhaStatus === ABHA_STATUS.VERIFIED;
  const needsPractitioner = !!record.needsPractitioner;
  // OR-106: the Doctor row is the picker while the form is editable; a frozen invoice only after its retry was refused for want of a
  // doctor. Not while loading: a slot summary is not yet known to be frozen, and the picker fetches the doctors once on mount.
  const showDoctorPicker = needsPractitioner && !loading && !loadError && (!readOnly || doctorAsked);
  const doctorToSend = showDoctorPicker && practitionerId ? practitionerId : undefined;
  // OR-108: a PAUSED slot (patient blocked) cannot be linked now: the row's own sentence is the reason, the doctor is not asked for.
  const pausedText = slot?.state === SLOT_STATE.PAUSED ? pausedSentenceOf(slot) : null;

  const applyInvoice = (inv) => {
    const f = formFrom(inv);
    setSaved(inv);
    setBillNumber(f.billNumber);
    setInvoiceType(f.invoiceType);
    setRows(f.rows);
    setDirty(false);
    setServerErr(null);
  };

  // Focus the drawer on open, lock page scroll behind it, restore both on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    asideRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, []);

  // ONE request per open: the invoice (slot summary given) or the prefill (suggestion). Blank = none.
  useEffect(() => {
    if (!needsLoad || loadStartedRef.current) return;
    loadStartedRef.current = true;
    (async () => {
      try {
        if (invoice) {
          applyInvoice((await getInvoice({ invoiceId: invoice.invoiceId })).invoice);
        } else {
          const p = await getInvoicePrefill({
            encounterKind: record.encounterKind,
            encounterId: record.encounterId,
            sourceKind: suggestion.sourceKind,
            sourceRef: suggestion.sourceRef,
          });
          const f = formFrom(p);
          setBillNumber(f.billNumber);
          setInvoiceType(f.invoiceType);
          setRows(f.rows);
        }
      } catch (err) {
        setLoadError(err?.code && !GENERIC_CODES.has(err.code) && err.userMessage ? err.userMessage : INVOICE.loadError);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once per open; the key remounts per invoice

  useEffect(() => {
    if (banner && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [banner]);

  // A 422 PRACTITIONER_REQUIRED takes focus to the picker (the browser scrolls it into view).
  useEffect(() => {
    if (doctorErr) doctorBoxRef.current?.focus();
  }, [doctorErr]);

  // Esc: closes a confirm modal first, then the drawer. Ignored while a request is in flight.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') keyRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ── Derived: parsed lines, live totals, client errors (one pass per edit) ──
  const parsed = useMemo(() => rows.map((r) => parseRow(r, gst)), [rows, gst]);
  const totals = useMemo(() => computeInvoiceTotals(parsed.map((p) => p.line)), [parsed]);
  const billError = billNumber.trim() ? null : INVOICE.err.billNumber;
  const typeError = invoiceType ? null : INVOICE.err.billType;
  const formError = !totals ? null
    : totals.totalNetPaise < 0 ? INVOICE.err.discountsOverRates
      : totals.totalGrossPaise <= 0 ? INVOICE.err.totalNotPositive : null;
  const hasClientErrors = !!(billError || typeError || formError || parsed.some((p) => p.errors));

  const lineServerErr = (i, key) => (serverErr && serverErr.lineIndex === i && serverErr.fieldKey === FIELD_KEY[key] ? serverErr.message : undefined);
  const lineErr = (i, key) => (showErrors && parsed[i].errors?.[key]) || lineServerErr(i, key);
  const fieldServerErr = (key) => (serverErr && serverErr.lineIndex == null && serverErr.fieldKey === key ? serverErr.message : null);
  const billMsg = (showErrors && billError) || fieldServerErr('billNumber');
  const typeMsg = (showErrors && typeError) || fieldServerErr('invoiceType');

  const summary = !showErrors ? [] : [
    billError, typeError, formError,
    ...parsed.flatMap((p, i) => (p.errors ? [INVOICE.lineError(i + 1, Object.values(p.errors).join(' '))] : [])),
  ].filter(Boolean);

  // ── Edits (stable callbacks: the memoised rows depend on them) ──
  const onLineChange = useCallback((index, key, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
    setDirty(true);
    setServerErr((e) => (e && e.lineIndex === index && e.fieldKey === FIELD_KEY[key] ? null : e));
  }, []);
  const onRemove = useCallback((index) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((_r, i) => i !== index) : prev));
    setDirty(true);
    setServerErr(null); // line indexes moved
  }, []);
  const addLine = () => {
    const row = newRow();
    setRows((prev) => [...prev, row]);
    setAddedId(row.id);
    setDirty(true);
  };
  const onBillNumber = (v) => {
    setBillNumber(v);
    setDirty(true);
    setServerErr((e) => (e && e.fieldKey === 'billNumber' ? null : e));
  };
  const applyType = (t) => {
    if (invoiceType === INVOICE_TYPE.PHARMACY && t !== INVOICE_TYPE.PHARMACY) {
      setRows((prev) => prev.map((r) => (r.cgst || r.sgst ? { ...r, cgst: '', sgst: '' } : r)));
    }
    setInvoiceType(t);
    setDirty(true);
    setServerErr((e) => (e && e.fieldKey === 'invoiceType' ? null : e));
  };
  const onDoctor = useCallback((id) => {
    setPractitionerId(id);
    setDoctorErr(false);
  }, []);
  const onType = (t) => {
    if (t === invoiceType || readOnly) return;
    if (invoiceType === INVOICE_TYPE.PHARMACY && rows.some((r) => typedGst(r.cgst) || typedGst(r.sgst))) {
      setPendingType(t);
      setConfirm(CONFIRM_KIND.GST);
    } else {
      applyType(t);
    }
  };

  // ── Server calls ──
  const finish = (inv, linked = false) => {
    unreportedRef.current = null;
    if (linked) {
      onDone(inv, {
        patientName: record.patientName,
        records: [{ invoiceId: inv.invoiceId, name: INVOICE.card.rowHead(inv), state: inv.state || SLOT_STATE.LINKING }],
      });
    } else {
      onDone(inv);
    }
    onClose();
  };
  const close = () => {
    if (unreportedRef.current) onDone(unreportedRef.current); // a save happened: let the parent refresh
    onClose();
  };
  const requestClose = () => {
    if (busyRef.current) return;
    if (dirty && !readOnly) setConfirm(CONFIRM_KIND.DISCARD);
    else close();
  };

  const persist = async () => {
    const body = { billNumber: billNumber.trim(), invoiceType, lines: parsed.map((p) => p.line) };
    const { invoice: inv } = saved
      ? await updateInvoice({ invoiceId: saved.invoiceId, ...body, version: saved.version })
      : await createInvoice({ encounterKind: record.encounterKind, encounterId: record.encounterId, ...body, prefillSource });
    setSaved(inv);
    setDirty(false);
    unreportedRef.current = inv;
    return inv;
  };

  const showError = async (err, fallback) => {
    if (err?.code === PRACTITIONER_REQUIRED && needsPractitioner) {
      setDoctorAsked(true); // nothing stored for this visit: the picker on the Doctor row is the answer (OR-106)
      setDoctorErr(true);
      return;
    }
    const serverText = err?.code === 'INCOMPLETE_RECORD'
      ? incompleteRecordText(err.missing, err.userMessage) // names the missing field and where to fix it (redesign v2)
      : err?.code && !GENERIC_CODES.has(err.code) && err.userMessage ? err.userMessage : null;
    const current = unreportedRef.current || saved; // a save earlier in this same click is not in `saved` yet
    if ((err?.code === 'VERSION_CONFLICT' || err?.code === 'INVOICE_LOCKED') && current) {
      try {
        const { invoice: fresh } = await getInvoice({ invoiceId: current.invoiceId }); // reload the latest copy
        applyInvoice(fresh);
        unreportedRef.current = fresh;
        setBanner(err.code === 'VERSION_CONFLICT' ? INVOICE.err.reloaded : serverText);
      } catch {
        setBanner(INVOICE.loadError);
      }
      return;
    }
    const inline = err?.fieldKey && err.fieldKey !== 'lines' && serverText;
    if (inline) setServerErr({ fieldKey: err.fieldKey, lineIndex: err.lineIndex ?? null, message: serverText });
    setBanner(inline ? '' : serverText || fallback); // 'lines' (e.g. INVOICE_TOO_LARGE) and whole-request codes: plainly, on top
  };

  const validate = () => {
    setShowErrors(true);
    if (hasClientErrors) {
      setBanner('');
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
      return false;
    }
    return true;
  };

  const onSave = async () => {
    if (busyRef.current || !validate()) return;
    busyRef.current = true;
    setBusy(BUSY.SAVE);
    setBanner('');
    let inv = null;
    try {
      inv = await persist();
    } catch (err) {
      await showError(err, INVOICE.err.saveFailed);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
    if (inv) finish(inv);
  };

  const onLinkClick = () => {
    if (busyRef.current || !validate()) return;
    setConfirm(CONFIRM_KIND.LINK);
  };

  const doLink = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setConfirm(null);
    setBusy(BUSY.LINK);
    setBanner('');
    let linked = null;
    try {
      const draft = !saved || dirty ? await persist() : saved; // unsaved changes are saved first
      ({ invoice: linked } = await linkInvoice({ invoiceId: draft.invoiceId, version: draft.version, practitionerId: doctorToSend }));
    } catch (err) {
      await showError(err, INVOICE.err.linkFailed);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
    if (linked) finish(linked, true);
  };

  // A frozen invoice: re-link it as it is (no save; no doctor until the server asks for one). See the header comment.
  const doRetry = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(BUSY.LINK);
    setBanner('');
    let linked = null;
    try {
      ({ invoice: linked } = await linkInvoice({ invoiceId: saved.invoiceId, version: saved.version, practitionerId: doctorToSend }));
    } catch (err) {
      await showError(err, INVOICE.err.linkFailed);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
    if (linked) finish(linked, true);
  };

  keyRef.current = () => {
    if (busyRef.current) return;
    if (confirm) setConfirm(null);
    else requestClose();
  };

  // ── Header facts ──
  const dateText = formatDateIST(isOpd ? (record.visitDate || record.encounterAt) : (record.dischargedAt || record.encounterAt));
  const encounterText = (isOpd
    ? [LIST.kinds.OPD, formatDateTimeISTWithYearNoSeconds(record.encounterAt),
      record.tokenNumber != null ? CARD.token(formatToken(record.tokenNumber, record.appointmentNumber, { hash: false })) : null,
      record.departmentName]
    : [LIST.kinds.IPD, record.ipNumber,
      record.admittedAt && record.dischargedAt ? CARD.admittedDischarged(formatDateIST(record.admittedAt), formatDateIST(record.dischargedAt)) : null,
      record.departmentName]
  ).filter(Boolean).join(INVOICE.sep);
  const doctorText = record.doctorName ? `Dr. ${record.doctorName.replace(/^dr\.?\s+/i, '')}` : '—';
  const taxesPaise = totals ? totals.totalGrossPaise - totals.totalNetPaise : null;
  const money = (p) => (p == null ? '—' : formatPaise(p));
  const typeLabel = invoiceType ? INVOICE_TYPE_LABEL[invoiceType] : '';
  const doctorReason = showDoctorPicker && !practitionerId ? PRACTITIONER.pickReason : null; // why Link is off until a doctor is picked
  const linkReason = !verified ? INVOICE.needsVerify : pausedText || doctorReason;
  const reasonShown = !!linkReason && !busy && (!readOnly || showDoctorPicker || !!pausedText); // a frozen invoice explains only the pause and the doctor
  const statusChip = readOnly
    ? <span className={canRetry ? CHIP_WARN : CHIP_OK}>{{ [SLOT_STATE.LINK_FAILED]: INVOICE.failedChip, [SLOT_STATE.READY]: INVOICE.notLinked }[saved.state] || INVOICE.linkedChip}</span>
    : <span className={CHIP_MUTED}>{dirty ? INVOICE.unsaved : INVOICE.notLinked}</span>;
  const formDisabled = loading || !!loadError || !!busy;
  const doctorPick = (
    <div
      ref={doctorBoxRef}
      tabIndex={-1}
      data-testid="invoice-doctor-pick"
      className={`min-w-0 flex-1 rounded-lg outline-none [&_[data-testid=hip-practitioner-picker]]:mb-0${doctorErr ? ' ring-2 ring-red-600 ring-offset-2 ring-offset-slate-50 dark:ring-red-400 dark:ring-offset-slate-800' : ''}`}
    >
      <fieldset disabled={formDisabled} className="m-0 min-w-0 border-0 p-0">
        <HipPractitionerPicker value={practitionerId} onChange={onDoctor} showReason={false} showLabel={false} />
      </fieldset>
      {doctorErr && <p role="alert" className={`m-0 mt-1 ${ERR}`}>{PRACTITIONER.pickReason}</p>}
    </div>
  );
  const facts = [
    [INVOICE.patient, `${record.patientName}${INVOICE.sep}${record.uhid}`,
      <span className={verified ? CHIP_OK : CHIP_WARN}>{verified ? ABHA_VERIFIED : ABHA_NOT_VERIFIED}</span>],
    [INVOICE.visit[record.encounterKind], encounterText, null],
    [INVOICE.doctor, doctorText, null, showDoctorPicker ? doctorPick : null],
    [INVOICE.billDate, dateText, <span className={MUTED}>{INVOICE.billDateFrom[record.encounterKind]}</span>],
  ];

  return (
    <div className="fixed inset-0" style={{ zIndex: DRAWER_Z }} data-testid="hip-invoice-drawer-root">
      <div role="presentation" data-testid="hip-invoice-overlay" className="absolute inset-0 bg-black/50" onClick={requestClose} />
      <aside
        ref={asideRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        className="absolute inset-x-0 bottom-0 top-14 flex flex-col rounded-t-[18px] border-t border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-[0_-10px_28px_rgba(0,0,0,0.3)] outline-none sm:inset-x-auto sm:right-0 sm:top-0 sm:w-[760px] sm:max-w-full sm:rounded-none sm:border-l sm:border-t-0 sm:shadow-[-10px_0_28px_rgba(0,0,0,0.28)]"
      >
        <div className="flex items-start justify-between gap-2.5 border-b border-slate-300 dark:border-slate-700 px-4 py-3.5">
          <div className="min-w-0">
            <h3 id={headingId} className="m-0 text-base font-bold">{INVOICE.title}</h3>
            <p className={`mt-0.5 ${MUTED}`}>
              {readOnly && saved.linkedAt ? INVOICE.linkedSubtitle(formatDateTimeISTWithYearNoSeconds(saved.linkedAt)) : INVOICE.subtitle}
            </p>
          </div>
          <div className="flex flex-none items-center gap-2">
            {statusChip}
            <button type="button" className={`${BTN_SM} h-8 w-8 !p-0`} aria-label={INVOICE.close} disabled={!!busy} onClick={requestClose}>✕</button>
          </div>
        </div>

        <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-auto px-4 py-3.5">
          {(banner || loadError) && (
            <div role="alert" className="flex-none rounded-lg border border-red-400 bg-red-50 dark:border-red-700 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-200">{loadError || banner}</div>
          )}
          {summary.length > 0 && (
            <div role="alert" data-testid="invoice-error-summary" className="flex-none rounded-lg border border-red-400 bg-red-50 dark:border-red-700 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-200">
              <b className="block">{INVOICE.errorsTitle}</b>
              <ul className="m-0 mt-1 list-disc pl-5">
                {summary.slice(0, SUMMARY_MAX).map((m, i) => <li key={i}>{m}</li>)}
              </ul>
              {summary.length > SUMMARY_MAX && <span className="text-xs font-semibold">{INVOICE.moreErrors(summary.length - SUMMARY_MAX)}</span>}
            </div>
          )}
          {loading && <p className={`flex-none ${MUTED}`} role="status">{INVOICE.loading}</p>}
          {readOnly && <p className={`flex-none ${MUTED}`}>{INVOICE.linkedNote}</p>}
          {!readOnly && !loading && INVOICE.prefillNote[prefillSource.kind] && (
            <p className={`flex-none ${MUTED}`}>{INVOICE.prefillNote[prefillSource.kind]}</p>
          )}

          <div className={`flex-none ${CARD_BOX} flex flex-col gap-1.5`}>
            {facts.map(([label, text, extra, block]) => (
              <div key={label} className={block ? 'flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2' : 'flex items-center gap-2'}>
                <span className={`w-[82px] flex-none text-[11px] font-bold uppercase tracking-wide text-slate-600 dark:text-slate-400${block ? ' sm:pt-5' : ''}`}>{label}</span>
                {block || (
                  <>
                    <span className="min-w-0 flex-1 break-words text-[12.5px] font-semibold">{text}</span>
                    {extra && <span className="flex-none">{extra}</span>}
                  </>
                )}
              </div>
            ))}
          </div>

          {!loading && !loadError && (
            <>
              <div className="flex-none">
                <span id={`${uid}-types`} className={LABEL}>{INVOICE.billType}</span>
                <div role="radiogroup" aria-labelledby={`${uid}-types`} className="flex flex-wrap gap-1.5">
                  {INVOICE_TYPES_BY_KIND[record.encounterKind].map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={invoiceType === t}
                      disabled={formDisabled || (readOnly && invoiceType !== t)}
                      className={typeChipClass(invoiceType === t, readOnly)}
                      onClick={() => onType(t)}
                    >
                      {INVOICE_TYPE_LABEL[t]}
                    </button>
                  ))}
                </div>
                {typeMsg && <p className={`mt-1 ${ERR}`}>{typeMsg}</p>}
              </div>

              <div className="flex-none">
                <label htmlFor={billId} className={LABEL}>
                  {INVOICE.billNumber}{!readOnly && <span className="font-semibold text-slate-600 dark:text-slate-400">{INVOICE.sep}{INVOICE.billNumberRequired}</span>}
                </label>
                {readOnly ? (
                  <p id={billId} className="m-0 text-sm font-semibold">{saved.billNumber}</p>
                ) : (
                  <>
                    <input
                      id={billId}
                      type="text"
                      className={`${inputClass(!!billMsg)} sm:max-w-[280px]`}
                      maxLength={BILL_NUMBER_MAX}
                      value={billNumber}
                      disabled={formDisabled}
                      aria-invalid={billMsg ? 'true' : undefined}
                      onChange={(e) => onBillNumber(e.target.value)}
                    />
                    <p className={`mt-1 ${billMsg ? ERR : MUTED}`}>{billMsg || INVOICE.billNumberHelper}</p>
                  </>
                )}
              </div>

              <div className="flex-none">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className={`${LABEL} !mb-0`}>{INVOICE.items(readOnly ? saved.lines.length : rows.length)}</span>
                  {!readOnly && <button type="button" className={BTN_SM} disabled={formDisabled} onClick={addLine}>{INVOICE.addLine}</button>}
                </div>
                {readOnly ? <ReadOnlyLines lines={saved.lines} gst={gst} /> : (
                  <table className="w-full min-w-[560px] border-collapse">
                    <thead>
                      <tr>
                        <th className={TH}>{INVOICE.col.n}</th>
                        <th className={TH}>{INVOICE.col.item}</th>
                        <th className={TH}>{INVOICE.col.rate}</th>
                        <th className={TH}>{INVOICE.col.discount} <span className="font-semibold text-slate-500 dark:text-slate-400">{INVOICE.col.optional}</span></th>
                        {gst && <th className={TH}>{INVOICE.col.cgst}</th>}
                        {gst && <th className={TH}>{INVOICE.col.sgst}</th>}
                        <th className={TH}><span className="sr-only">✕</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <LineRow
                          key={row.id}
                          row={row}
                          index={i}
                          gst={gst}
                          canRemove={rows.length > 1}
                          autoFocus={row.id === addedId}
                          disabled={formDisabled}
                          onChange={onLineChange}
                          onRemove={onRemove}
                          errDescription={lineErr(i, 'description')}
                          errRate={lineErr(i, 'rate')}
                          errDiscount={lineErr(i, 'discount')}
                          errCgst={gst ? lineErr(i, 'cgst') : undefined}
                          errSgst={gst ? lineErr(i, 'sgst') : undefined}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col gap-2 border-t border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3">
          {!loading && !loadError && (
            <div data-testid="invoice-totals" className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
              <span className="whitespace-nowrap">{INVOICE.net} <b data-testid="invoice-net">{money(readOnly ? saved.totalNetPaise : totals?.totalNetPaise)}</b></span>
              {gst && <span className="whitespace-nowrap">{INVOICE.taxes} <b data-testid="invoice-taxes">{money(readOnly ? saved.totalGrossPaise - saved.totalNetPaise : taxesPaise)}</b></span>}
              <span className="whitespace-nowrap">{INVOICE.gross} <b data-testid="invoice-gross" className="text-base">{money(readOnly ? saved.totalGrossPaise : totals?.totalGrossPaise)}</b></span>
              <span className={`basis-full ${MUTED}`}>{!readOnly && !totals ? INVOICE.totalsPending : INVOICE.totalsHelp}</span>
              {showErrors && formError && <span className={`basis-full ${ERR}`}>{formError}</span>}
            </div>
          )}
          {reasonShown && <p id={reasonId} className="m-0 text-xs font-semibold text-slate-700 dark:text-slate-300">{linkReason}</p>}
          <div className="flex items-center justify-end gap-2.5">
            {readOnly ? (
              <>
                <button type="button" className={BTN} disabled={!!busy} onClick={requestClose}>{INVOICE.close}</button>
                {canRetry && (
                  <button
                    type="button"
                    className={BTN_PRIMARY}
                    disabled={!!busy || !!linkReason}
                    aria-describedby={reasonShown ? reasonId : undefined}
                    onClick={doRetry}
                  >
                    {busy === BUSY.LINK ? INVOICE.linking : (saved.state === SLOT_STATE.LINK_FAILED ? INVOICE.retryLink : INVOICE.linkToAbha)}
                  </button>
                )}
              </>
            ) : (
              <>
                <button type="button" className={BTN} disabled={!!busy} onClick={requestClose}>{INVOICE.cancel}</button>
                <button type="button" className={BTN} disabled={formDisabled} onClick={onSave}>{busy === BUSY.SAVE ? INVOICE.saving : INVOICE.saveDraft}</button>
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  disabled={formDisabled || !!linkReason}
                  aria-describedby={reasonShown ? reasonId : undefined}
                  onClick={onLinkClick}
                >
                  {busy === BUSY.LINK ? INVOICE.linking : INVOICE.linkToAbha}
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      <ConfirmationModal
        isOpen={confirm === CONFIRM_KIND.LINK}
        icon={null}
        title={INVOICE.confirmLinkTitle}
        message={INVOICE.confirmLinkBody(typeLabel, billNumber.trim(), record.patientName, rows.length)}
        ctaLabel={INVOICE.linkToAbha}
        cancelLabel={INVOICE.cancel}
        onConfirm={doLink}
        onCancel={() => setConfirm(null)}
      >
        {totals && <p className="text-sm font-semibold">{INVOICE.confirmLinkTotals(money(totals.totalNetPaise), money(taxesPaise), money(totals.totalGrossPaise))}</p>}
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={confirm === CONFIRM_KIND.GST}
        icon={null}
        title={INVOICE.clearGstTitle}
        message={INVOICE.clearGstBody(pendingType ? INVOICE_TYPE_LABEL[pendingType] : '')}
        ctaLabel={INVOICE.clearGstCta}
        cancelLabel={INVOICE.cancel}
        onConfirm={() => { setConfirm(null); applyType(pendingType); }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmationModal
        isOpen={confirm === CONFIRM_KIND.DISCARD}
        icon={null}
        variant="danger"
        autoFocusCancel
        title={INVOICE.discardTitle}
        message={INVOICE.discardBody}
        ctaLabel={INVOICE.discardCta}
        cancelLabel={INVOICE.keepEditing}
        onConfirm={() => { setConfirm(null); close(); }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

// ── LINKED: the frozen snapshot, text only ──────────────────────────────────────────────
function ReadOnlyLines({ lines, gst }) {
  const cell = (p) => (p ? formatPaise(p) : '—');
  return (
    <table className="w-full min-w-[520px] border-collapse" data-testid="invoice-readonly-lines">
      <thead>
        <tr>
          <th className={TH}>{INVOICE.col.n}</th>
          <th className={TH}>{INVOICE.col.item}</th>
          <th className={TH}>{INVOICE.col.rate}</th>
          <th className={TH}>{INVOICE.col.discount}</th>
          {gst && <th className={TH}>{INVOICE.col.cgst}</th>}
          {gst && <th className={TH}>{INVOICE.col.sgst}</th>}
        </tr>
      </thead>
      <tbody>
        {lines.map((l, i) => (
          // eslint-disable-next-line react/no-array-index-key -- a frozen snapshot: lines never move
          <tr key={i} className="border-b border-slate-200 dark:border-slate-700 text-sm">
            <td className={`${TD} text-xs text-slate-600 dark:text-slate-400`}>{i + 1}</td>
            <td className={`${TD} break-words`}>{l.description}</td>
            <td className={`${TD} whitespace-nowrap text-right`}>{formatPaise(l.ratePaise)}</td>
            <td className={`${TD} whitespace-nowrap text-right`}>{cell(l.discountPaise)}</td>
            {gst && <td className={`${TD} whitespace-nowrap text-right`}>{cell(l.cgstPaise)}</td>}
            {gst && <td className={`${TD} whitespace-nowrap text-right`}>{cell(l.sgstPaise)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
